import React, { useEffect } from 'react';
import { classifyError, getUserErrorMessage, logError, ErrorType } from '../lib/error-handler';
import Breadcrumb from '../components/Breadcrumb';
import { useNavigate } from '@tanstack/react-router';

interface ErrorComponentProps {
  error: Error;
  onReset?: () => void;
}

const ErrorComponent = ({ error, onReset }: ErrorComponentProps) => {
  const navigate = useNavigate();
  const appError = classifyError(error)
  const userMessage = getUserErrorMessage(appError)
  const isDev = import.meta.env.DEV

  // Log error on mount
  useEffect(() => {
    logError(appError, 'Route Error', {
      type: appError.type,
      status: appError.status,
    })
  }, [appError])

  // Determine action button based on error type
  const getActionButton = () => {
    if (appError.type === ErrorType.AUTH) {
      return (
        <button
          onClick={() => window.location.href = '/signin'}
          className="pixel-btn"
        >
          <i className="fas fa-sign-in-alt me-2"></i>
          Go to Sign In
        </button>
      );
    }

    if (appError.type === ErrorType.NETWORK || appError.type === ErrorType.SERVER) {
      return (
        <button
          onClick={() => window.location.reload()}
          className="pixel-btn"
        >
          <i className="fas fa-sync-alt me-2"></i>
          Try Again
        </button>
      );
    }

    // Default: go back (but this is now separate)
    return null;
  };

  // Get flat color based on error type (style guide tokens)
  const getHeaderColor = (): string => {
    switch (appError.type) {
      case ErrorType.AUTH: return 'var(--color-warning, #ffc107)'
      case ErrorType.NOT_FOUND: return 'var(--color-text-muted)'
      case ErrorType.VALIDATION: return 'var(--color-warning, #fd7e14)'
      case ErrorType.SERVER: return 'var(--color-error)'
      case ErrorType.NETWORK: return 'var(--color-accent-blue)'
      case ErrorType.RATE_LIMIT: return 'var(--color-accent-green)'
      default: return 'var(--color-error)'
    }
  }

  const errorTitle = getErrorTitle(appError.type);

  return (
    <>
      <main className="main-content mt-1 border-radius-lg">
      <div className="container-fluid">
        {/* Breadcrumb */}
        <Breadcrumb text1="Error" text2={errorTitle} />

        {/* Error Content */}
        <div className="row min-vh-100 align-items-center justify-content-center" style={{ minHeight: '500px' }}>
          <div className="col-lg-8 col-md-10 text-center">
            <div className="pixel-card">
            {/* Error Icon/Title */}
            <div className="mb-4">
              <h1
                className="display-1 fw-bold"
                style={{
                  fontSize: '10rem',
                  lineHeight: 1,
                  letterSpacing: '-0.05em',
                  color: getHeaderColor(),
                }}
              >
                {getErrorShortTitle(appError.type)}
              </h1>
              <div className="position-relative d-inline-block mt-3">
                <h2 className="h1 mb-0" style={{ color: 'var(--color-text-heading)' }}>{errorTitle}</h2>
                <div className="position-absolute bottom-0 start-0 w-100" style={{ height: '3px' }}>
                  <div
                    style={{ height: '100%', width: '120px', background: 'var(--color-accent-blue)' }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            <p
              className="mb-5"
              style={{
                fontSize: '1.1rem',
                maxWidth: '600px',
                margin: '0 auto 2rem',
                lineHeight: 1.6,
                color: 'var(--color-text-body)',
              }}
            >
              {userMessage}
            </p>

            {/* Action Buttons */}
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              {getActionButton()}

              <button
                onClick={() => navigate({ to: '/' }).catch(() => {})}
                className="pixel-btn-outline"
              >
                <i className="fas fa-home me-2"></i>
                Continue
              </button>
            </div>

            {/* Decorative Dots */}
            <div className="mt-5 position-relative" style={{ height: '80px' }}>
              <div className="position-absolute top-50 start-50 translate-middle">
                <div className="d-flex gap-3" style={{ transform: 'scale(1.5)' }}>
                  <div className="rounded-circle" style={{ width: '12px', height: '12px', background: 'var(--color-accent-blue)', opacity: 0.7, boxShadow: '0 0 10px var(--color-accent-blue)' }}></div>
                  <div className="rounded-circle" style={{ width: '12px', height: '12px', background: 'var(--color-accent-green)', opacity: 0.5, boxShadow: '0 0 10px var(--color-accent-green)' }}></div>
                  <div className="rounded-circle" style={{ width: '12px', height: '12px', background: 'var(--color-accent-blue-hover)', opacity: 0.8, boxShadow: '0 0 10px var(--color-accent-blue-hover)' }}></div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Technical details - only in development */}
        {isDev && (
          <div className="row mt-5">
            <div className="col-12">
              <div
                className="p-4 border-radius-lg"
                style={{
                  backgroundColor: '#1a1a2e',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <h3 className="text-white mb-3">Error Details (Dev Mode)</h3>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    fontSize: '14px',
                    color: '#adb5bd',
                    margin: 0,
                    fontFamily: 'monospace',
                    backgroundColor: '#0a0a0a',
                    padding: '15px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  {JSON.stringify({
                    message: error.message,
                    type: appError.type,
                    status: appError.status,
                    stack: error.stack,
                  }, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
    </>
  );
};

/**
 * Returns a short title for each error type (used in large display).
 */
function getErrorShortTitle(type: ErrorType): string {
  switch (type) {
    case ErrorType.AUTH: return '4'
    case ErrorType.NOT_FOUND: return '404'
    case ErrorType.VALIDATION: return '400'
    case ErrorType.SERVER: return '500'
    case ErrorType.NETWORK: return '!'
    case ErrorType.RATE_LIMIT: return '429'
    default: return '!'
  }
}

/**
 * Returns a full title for each error type.
 */
function getErrorTitle(type: ErrorType): string {
  switch (type) {
    case ErrorType.AUTH: return 'Session Expired'
    case ErrorType.NOT_FOUND: return 'Page Not Found'
    case ErrorType.VALIDATION: return 'Invalid Request'
    case ErrorType.SERVER: return 'Server Error'
    case ErrorType.NETWORK: return 'Connection Error'
    case ErrorType.RATE_LIMIT: return 'Too Many Requests'
    default: return 'Something went wrong'
  }
}

export default ErrorComponent;
